'use strict'

// React
import {useCallback, useEffect, useState} from 'react'

// Commerce SDK
import {
    useCustomer,
    useCustomerId,
    useCustomerType,
    useCategory
} from '@salesforce/commerce-sdk-react'

import {useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'

// OOTB Hooks
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

// Hooks
import {useCurrentBasket} from './use-current-basket'
import useShipToCountry from './use-ship-to-country'
import useMultiSite from './use-multi-site'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {CAT_MENU_DEFAULT_ROOT_CATEGORY} from '@salesforce/retail-react-app/app/constants'

import {
    getLocalStorageJSONItem,
    setLocalStorageJSONItem,
    clearLocalStorageJSONItem,
    getAppDomain
} from '@app/utils/utils'
// Custom Utils
import {
    buildAnalyticsPageViewData,
    buildAnalyticsRemoveFromCartData,
    buildAnalyticsSignUpEventData,
    buildAnalyticsLoginEventData,
    buildAnalyticsCheckoutData,
    buildAnalyticsNewsletterSubscriptionData,
    buildAnalyticsSearchEventData,
    buildAnalyticsClickRelatedProductsData,
    buildAnalyticsCartViewData,
    buildAnalyticsAddToWishlistData,
    buildAnalyticsViewItemListData,
    buildAnalyticsAddtoCartData,
    buildAnalyticsClickMenuData,
    buildAnalyticsViewItemData,
    buildAnalyticsApplyFilterData,
    buildAnalyticsApplyStoreFilterData,
    buildAnalyticsFamilyLabelData
} from '../utils/analytics-utils'

import {createFilterCategories} from '@app/utils/store-utils'

// Custom Contexts
import {useConversionRate} from '../custom-contexts'

/**
 * Custom hook for integrating Google Tag Manager (GTM) functionality within a React component.
 * Provides methods to trigger GTM events, including page view events, with relevant customer and page details.
 *
 * @returns {Object} An object containing methods to interact with GTM.
 * @returns {Function} return.triggerPageLoadEvent - A function to trigger a GTM page view event.
 *
 * @example
 * // Example usage within a React component
 * const { triggerPageLoadEvent } = useGTM();
 *
 * useEffect(() => {
 *     triggerPageLoadEvent();
 * }, [triggerPageLoadEvent]);
 */
export const useGTM = () => {
    const customerId = useCustomerId()
    const {isRegistered, customerType} = useCustomerType()
    const {data: customer, isLoading: isCustomerLoading} = useCustomer(
        {
            parameters: {customerId}
        },
        {enabled: !!customerId && isRegistered}
    )
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const {shipToCountry} = useShipToCountry()
    const {locale, site} = useMultiSite()
    const {currency: activeCurrency} = useCurrency()
    const conversionsRate = useConversionRate()
    const {data: categoriesTree} = useCategory({
        parameters: {
            id: CAT_MENU_DEFAULT_ROOT_CATEGORY,
            levels: 4,
            c_isMenu: true
        }
    })

    const currentLocation = useLocation()
    const [hasTriggeredFaqReferrer, setHasTriggeredFaqReferrer] = useState(false)

    const categoryLookup = buildCategoryLookup(categoriesTree)
    const domain = getAppDomain()
    // State
    const [pageType, setPageType] = useState(null)
    const {formatMessage} = useIntl()

    const storeFilterCategories = createFilterCategories(formatMessage)

    /**
     * Triggers a Google Tag Manager (GTM) event by updating the data layer with the specified event name and data.
     * This function clears any previous ecommerce data from the data layer before adding the new event details.
     *
     * @function
     * @param {string} eventName - The name of the event to be triggered in GTM.
     * @param {Object} [eventData={}] - An optional object containing additional data to be included in the GTM event.
     * @returns {void}
     */
    const triggerGTMEvent = useCallback((eventName, eventData = {}) => {
        // Clear the previous ecommerce object.
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
            ecommerce: null
        })

        // Send the new event to the data layer.
        window.dataLayer.push({
            event: eventName,
            ...eventData
        })
    }, [])

    /**
     * Triggers a Google Tag Manager (GTM) event for viewing a list of items.
     * This function processes a list of products in batches and sends each batch
     * as a separate GTM event to avoid exceeding the maximum number of items per event.
     *
     * @param {Array} products - An array of product objects to be included in the GTM event.
     * @param {number} maxItemsPerEvent - The maximum number of items allowed per GTM event batch.
     * @param {number} totalProducts - The total number of products to be processed.
     * @param {string} currency - The currency code used for the transaction, derived from the basket or active currency.
     *
     * @returns {void} This function does not return a value.
     */
    const triggerViewItemListEvent = (products, format) => {
        const maxItemsPerEvent = 15
        const totalProducts = products?.length ?? 0

        for (let i = 0; i < totalProducts; i += maxItemsPerEvent) {
            const productBatch = products.slice(i, i + maxItemsPerEvent)

            const finalEventData = {
                ecommerce: buildAnalyticsViewItemListData(
                    categoryLookup,
                    productBatch,
                    basket,
                    conversionsRate,
                    format
                )
            }

            triggerGTMEvent('view_item_list', finalEventData)
        }
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for removing a product from the cart.
     *
     * This function constructs the necessary analytics data for a 'remove from cart' event
     * and triggers the corresponding GTM event with the constructed data.
     *
     * @param {Object} product - The product object containing details about the product being removed.
     * @param {Object} productItem - The specific item instance of the product being removed from the cart.
     */
    const triggerRemoveFromCartEvent = (product, productItem) => {
        const finalEventData = {
            ecommerce: buildAnalyticsRemoveFromCartData(product, productItem, conversionsRate)
        }
        triggerGTMEvent('remove_from_cart', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) 'add_to_cart' event with the appropriate ecommerce data.
     * @param {Array<Object>} productItems - Array of product item objects being added to the cart.
     * @param {Object} finalProduct - The final product object representing the main product added.
     * @param {string} provenance - The source or context from which the add to cart event originated.
     * @returns {void}
     */
    const triggerAddToCartEvent = (productItems, finalProduct, provenance) => {
        const finalEventData = {
            ecommerce: buildAnalyticsAddtoCartData(
                productItems,
                finalProduct,
                conversionsRate,
                basket,
                provenance
            )
        }
        triggerGTMEvent('add_to_cart', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for adding a product to the wishlist.
     *
     * This function constructs the necessary event data for an "add_to_wishlist" event
     * and sends it to GTM. It utilizes the `buildAnalyticsAddToWishlistData` function
     * to generate the appropriate ecommerce data structure for the event.
     *
     * @param {Object} product - The product object containing details about the product
     *                            being added to the wishlist. This object is used to
     *                            build the analytics data for the GTM event.
     */
    const triggerAddtoWishListEvent = (product) => {
        const finalEventData = {
            ecommerce: buildAnalyticsAddToWishlistData(product, categoryLookup, conversionsRate)
        }
        triggerGTMEvent('add_to_wishlist', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for newsletter subscription.
     *
     * This function constructs the event data for a newsletter subscription
     * using the provided page position and sends it to GTM.
     *
     * @param {string} pagePosition - The position on the page where the newsletter subscription event is triggered.
     */
    const triggerNewsletterSubscriptionEvent = (pagePosition) => {
        const finalEventData = buildAnalyticsNewsletterSubscriptionData(pagePosition)
        triggerGTMEvent('newsletter_subscription', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for related product clicks.
     *
     * This function constructs the necessary analytics data for a related product click event
     * and triggers the GTM event with the specified event name and data.
     *
     * @param {Object} product - The product object containing details of the related product.
     * @param {string} event - The name of the event to be triggered in GTM.
     */
    const triggerClickRelatedProductsEvent = (product, event) => {
        const finalEventData = buildAnalyticsClickRelatedProductsData(product)
        triggerGTMEvent(event, finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event when the shipping destination country changes.
     *
     * This function checks if the old and new shipping destination countries are valid strings
     * and not null. If both conditions are met, it converts the country names to lowercase
     * and triggers a GTM event named 'popin_change_shipto' with the details of the country change.
     *
     * @param {string} oldShipToCountry - The previous shipping destination country.
     * @param {string} newShipToCountry - The new shipping destination country.
     */
    const triggerPopinChangeShipToEvent = (oldShipToCountry, newShipToCountry) => {
        triggerPageLoadEvent(getLocalStorageJSONItem('lastPageViewType'))
        if (
            typeof oldShipToCountry === 'string' &&
            oldShipToCountry !== null &&
            typeof newShipToCountry === 'string' &&
            newShipToCountry !== null
        ) {
            const oldCountryLower = oldShipToCountry.toLowerCase()
            const newCountryLower = newShipToCountry.toLowerCase()
            triggerGTMEvent('popin_change_shipto', {
                country_change: `${oldCountryLower} to ${newCountryLower}`
            })
        }
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for viewing the cart with the specified products.
     *
     * This function constructs the necessary analytics data for a cart view event and sends it to GTM.
     *
     * @param {Array} products - An array of product objects that are currently in the cart.
     * @returns {void}
     */
    const triggerViewCartEvent = (products) => {
        const finalEventData = {
            ecommerce: buildAnalyticsCartViewData(categoryLookup, basket, products, conversionsRate)
        }
        triggerGTMEvent('view_cart', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for search results.
     *
     * This function constructs the necessary analytics data for a search event
     * using the provided search term and then triggers a GTM event named
     * 'view_search_results' with the constructed data.
     *
     * @param {string} searchTerm - The search term entered by the user to be
     *                              included in the analytics event data.
     */
    const triggerSearchEvent = (searchTerm) => {
        const finalEventData = buildAnalyticsSearchEventData(searchTerm)
        triggerGTMEvent('view_search_results', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager event for guest checkout.
     * This function is used to signal that a guest checkout process has been initiated.
     * It utilizes the `triggerGTMEvent` function to send the event data to GTM.
     *
     * @function
     * @returns {void}
     */
    const triggerGuestCheckoutEvent = () => {
        triggerGTMEvent('guest_checkout', {})
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for the "Shop The Look" feature.
     * This function sends a 'click_shop_the_look' event to GTM with an empty data object.
     * It is used to track user interactions with the "Shop The Look" feature on the website.
     */
    const triggerShopTheLookEvent = () => {
        triggerGTMEvent('click_shop_the_look', {})
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for user sign-up.
     * This function constructs the necessary event data for a sign-up action
     * and sends it to GTM using the specified event name.
     *
     * @function
     * @returns {void}
     */
    const triggerSignUpEvent = () => {
        const finalEventData = buildAnalyticsSignUpEventData()
        triggerGTMEvent('sign_up', finalEventData)
    }

    /*
     * Triggers Google Tag Manager (GTM) events for checkout processes with specified product data.
     *
     * @param {Array} products - An array of product objects involved in the checkout process.
     * @param {string} event - The name of the GTM event to be triggered.
     */
    const triggerCheckoutEvents = (products, event, lineItemCrt) => {
        const finalEventData = {
            ecommerce: buildAnalyticsCheckoutData(
                lineItemCrt ? lineItemCrt : basket,
                products,
                conversionsRate,
                event,
                customerType
            )
        }
        triggerGTMEvent(event, finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for a menu click action.
     *
     * This function constructs the necessary event data for a menu click using the provided category
     * and then triggers a GTM event with the event name 'click_menu'.
     *
     * @param {string} category - The category of the menu item that was clicked. This is used to build
     *                            the analytics data for the event.
     */
    const triggerClickMenuEvent = (category) => {
        const finalEventData = buildAnalyticsClickMenuData(category)
        triggerGTMEvent('click_menu', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager event for a website error.
     *
     * This function sends an event to Google Tag Manager with a predefined
     * error message indicating a '404 page' error. It is used to log and
     * track error occurrences on the website.
     *
     **/
    const triggerErrorEvent = () => {
        triggerGTMEvent('error_website', {error_message: '404 page'})
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for user login.
     *
     * This function constructs the necessary analytics data for a login event
     * and sends it to GTM using the specified event name 'login'.
     *
     * @function
     * @returns {void}
     */
    const triggerLoginEvent = () => {
        const finalEventData = buildAnalyticsLoginEventData()
        triggerGTMEvent('login', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for viewing an item.
     *
     * This function constructs the necessary analytics data for a 'view_item' event
     * and triggers the corresponding GTM event with the constructed data.
     *
     * @param {Object} product - The product object containing details about the product being viewed.
     */
    const triggerViewItemEvent = (event, product) => {
        const finalEventData = {
            ecommerce: buildAnalyticsViewItemData(product, categoryLookup, conversionsRate)
        }
        triggerGTMEvent(event, finalEventData)
    }

    /**
     * Triggers a page load event by setting the page type.
     *
     * @param {string} type - The type of the page to be set, which determines the page load event.
     */
    const triggerPageLoadEvent = (type) => {
        setPageType(type)
    }

    /**
     * Custom React hook that tracks page load events and manages cookie-related GTM (Google Tag Manager) events.
     * Intended for use within the GTM integration layer to ensure proper event firing on page load and cookie changes.
     * @returns {void}
     */
    const usePageLoadAndCookies = (pageName) => {
        const [hasMounted, setHasMounted] = useState(false)
        const isCookiesChanged = getLocalStorageJSONItem('cookiesAccepted')

        useEffect(() => {
            if (!hasMounted) {
                setHasMounted(true)
                triggerPageLoadEvent(pageName)
            } else if (typeof isCookiesChanged !== 'undefined') {
                triggerPageLoadEvent(pageName)
            }
        }, [isCookiesChanged, hasMounted, pageName])
    }

    const [isPageViewTriggered, setIsPageViewTriggered] = useState(false)
    /**
     * Triggers a Google Tag Manager (GTM) event when the size guide is clicked.
     * Typically used to track user interactions with the size guide component for analytics purposes.
     * @function
     * @returns {void}
     */
    const triggerClickSizeGuideEvent = () => {
        const finalEventData = {
            link_text: 'find_my_size'
        }
        triggerGTMEvent('click_size_guide', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager event for the Contact Us form submission.
     * Typically used to send form interaction data to GTM for analytics or marketing purposes.
     * @function
     * @param {Object} formData - The data submitted via the Contact Us form.
     * @param {string} formData.name - The name entered by the user.
     * @param {string} formData.email - The email address entered by the user.
     * @param {string} formData.message - The message content submitted by the user.
     * @returns {void}
     */
    const triggerContactUsFormEvent = () => {
        triggerGTMEvent('contact_form_submission')
    }

    /**
     * Triggers a custom event for the bespoke form, typically used for Google Tag Manager (GTM) integration.
     * This function is responsible for dispatching an event when the bespoke form is interacted with,
     * allowing tracking or analytics scripts to capture the event details.
     *
     * @function
     * @param {Object} formData - The data associated with the bespoke form submission or interaction.
     * @param {string} eventName - The name of the event to be triggered for GTM.
     * @returns {void}
     */
    const triggerBespokeFormEvent = () => {
        triggerGTMEvent('bespoke_form_submission')
    }

    /**
     * Triggers a Google Tag Manager event when a catalog tab is clicked.
     * @function triggerCatalogTabClickEvent
     * @param {string} tabName - The name of the catalog tab that was clicked.
     * @param {Object} [additionalData] - Optional additional data to include in the GTM event.
     * @returns {void}
     */
    const triggerCatalogTabClickEvent = (location, categoryName) => {
        const finalEventData = {
            link_url: domain + location,
            link_text: categoryName
        }
        triggerGTMEvent('click_catalog_tab', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event when a FAQ link is clicked.
     * Typically used to track user interactions with FAQ links for analytics purposes.
     * @function
     * @param {string} questionId - The unique identifier of the FAQ question clicked.
     * @param {string} questionText - The text of the FAQ question clicked.
     * @returns {void}
     */
    const triggerFaqLinkClickEvent = (location, linkText) => {
        const finalEventData = {
            link_url: location,
            link_text: linkText
        }
        if (location.indexOf(domain) > -1) {
            setLocalStorageJSONItem('navigatedFrom', currentLocation?.pathname)
        }

        triggerGTMEvent('faq_links', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event when a FAQ accordion item is clicked.
     * Typically used to track user interactions with FAQ sections for analytics purposes.
     * @function
     * @param {string} question - The FAQ question that was clicked.
     * @param {number} index - The index of the clicked FAQ item within the list.
     * @returns {void}
     */
    const triggerFaqAccordionClickEvent = () => {
        triggerGTMEvent('faq_sections_used')
    }

    /**
     * Triggers a Google Tag Manager (GTM) event when an image menu item is clicked.
     * Typically used to track user interactions with image-based menu elements for analytics purposes.
     * @function
     * @returns {void}
     */
    const triggerClickImageMenuEvent = (location) => {
        const finalEventData = {
            link_url: location
        }
        triggerGTMEvent('click_image_menu', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event when a footer link is clicked.
     * Typically used to track user interactions with footer links for analytics purposes.
     * @function
     * @param {string} linkName - The name or identifier of the footer link clicked.
     * @param {Object} [additionalData] - Optional. Additional data to include in the GTM event payload.
     * @returns {void}
     */
    const triggerFooterLinkEvent = (location, text) => {
        const finalEventData = {
            link_url: location.startsWith('/') ? domain + location : location,
            link_text: text
        }
        triggerGTMEvent('click_footer', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event when the store locator is clicked.
     * This function is typically used to send analytics data to GTM for tracking user interactions
     * with the store locator component.
     *
     * @function
     * @returns {void}
     */
    const triggerClickStoreLocatorEvent = (storeID, storeName) => {
        const finalEventData = {
            store_loc_shop: storeName,
            store_loc_id: storeID
        }
        triggerGTMEvent('store_locator_phone', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager event when a user clicks on a search suggestion.
     * Typically used to track user interactions with autocomplete or search suggestion features.
     * @function
     * @param {Object} suggestion - The search suggestion object that was clicked.
     * @param {string} suggestion.value - The value or label of the clicked suggestion.
     * @param {number} [suggestion.position] - The position of the suggestion in the list (optional).
     * @returns {void}
     */
    const triggerClickSearchSuggestionEvent = (text) => {
        const finalEventData = {
            link_text: text
        }
        triggerGTMEvent('click_suggested_search', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event when a user interacts with filter elements.
     * Typically used to track filter click events for analytics purposes.
     * @function
     * @param {string} filterName - The name or identifier of the filter that was clicked.
     * @param {string} filterValue - The value selected or toggled by the user.
     * @param {Object} [additionalData] - Optional. Additional event data to send with the GTM event.
     * @returns {void}
     */
    const triggerClickFiltersEvent = (refinementsData, sortingData) => {
        const finalEventData = buildAnalyticsApplyFilterData(refinementsData, sortingData)
        triggerGTMEvent('click_filter', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event when a product quick view is shown.
     * Typically used to send analytics data for product impressions or interactions.
     * @function
     * @returns {void}
     */
    const triggerQuickViewShowEvent = (product) => {
        const finalEventData = buildAnalyticsViewItemData(product, categoryLookup, conversionsRate)
        triggerGTMEvent('click_quick_shop', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager event when store filters are clicked.
     * Used to track user interactions with store filter options for analytics purposes.
     * @function
     * @param {Object} filters - The filters applied by the user (e.g., location, store type).
     * @param {string} filters.category - The category of the filter applied.
     * @param {string} [filters.value] - The specific value selected for the filter.
     * @returns {void}
     */
    const triggerClickStoreFiltersEvent = (searchTerm, filters) => {
        const finalEventData = buildAnalyticsApplyStoreFilterData(
            storeFilterCategories,
            searchTerm,
            filters
        )
        triggerGTMEvent('store_locator_filter', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager event for clicks on 'family' product labels.
     * @param {string} productTag - The tag associated with the product, used to determine if it is a 'family' label.
     * @param {string} linkAddress - The relative URL or address of the clicked link.
     * @returns {void}
     */
    const triggerClickFamilyLabel = (productTag, linkAddress) => {
        if (productTag && productTag.toLowerCase() === 'family') {
            const fullLocation = domain + linkAddress
            const finalEventData = buildAnalyticsFamilyLabelData(productTag, fullLocation)
            triggerGTMEvent('click_family_label', finalEventData)
        }
    }

    useEffect(() => {
        const isGuest = customerType === 'guest'
        const isCustomerDataReady = !isCustomerLoading || isGuest
        const countryCodeStillSetting = getLocalStorageJSONItem('countryCode')

        if (
            isCustomerDataReady &&
            !isBasketLoading &&
            pageType &&
            basket &&
            !countryCodeStillSetting
        ) {
            const finalEventData = buildAnalyticsPageViewData(
                pageType,
                customer,
                basket,
                shipToCountry,
                locale,
                site,
                customerType,
                activeCurrency
            )
            triggerGTMEvent('page_view', finalEventData)
            setLocalStorageJSONItem('lastPageViewType', pageType)
            setPageType(null) // Reset pageType after event is triggered
            setIsPageViewTriggered(true)
        }
        const isFromFAQ = getLocalStorageJSONItem('navigatedFrom')
        if (
            !hasTriggeredFaqReferrer &&
            currentLocation?.pathname &&
            !currentLocation?.pathname.endsWith('/page/faq') &&
            isFromFAQ
        ) {
            const fullLocation = domain + isFromFAQ
            triggerGTMEvent('faq_referrer', {
                page_referrer_faq: fullLocation
            })
            setHasTriggeredFaqReferrer(true)
            clearLocalStorageJSONItem('navigatedFrom')
        }
    }, [
        isCustomerLoading,
        isBasketLoading,
        pageType,
        customer,
        basket,
        shipToCountry,
        locale,
        site,
        customerType,
        activeCurrency,
        triggerGTMEvent
    ])

    return {
        triggerPageLoadEvent,
        triggerRemoveFromCartEvent,
        triggerAddtoWishListEvent,
        triggerNewsletterSubscriptionEvent,
        triggerClickRelatedProductsEvent,
        triggerSearchEvent,
        triggerGuestCheckoutEvent,
        triggerSignUpEvent,
        triggerLoginEvent,
        triggerCheckoutEvents,
        triggerViewCartEvent,
        triggerViewItemListEvent,
        triggerAddToCartEvent,
        triggerClickMenuEvent,
        triggerErrorEvent,
        triggerShopTheLookEvent,
        triggerPopinChangeShipToEvent,
        triggerViewItemEvent,
        usePageLoadAndCookies,
        isPageViewTriggered,
        triggerClickSizeGuideEvent,
        triggerContactUsFormEvent,
        triggerBespokeFormEvent,
        triggerCatalogTabClickEvent,
        triggerFaqAccordionClickEvent,
        triggerFaqLinkClickEvent,
        triggerClickImageMenuEvent,
        triggerFooterLinkEvent,
        triggerClickStoreLocatorEvent,
        triggerClickSearchSuggestionEvent,
        triggerClickFiltersEvent,
        triggerQuickViewShowEvent,
        triggerClickStoreFiltersEvent,
        triggerClickFamilyLabel
    }
}

/**
 * Builds a lookup object mapping each category ID to its hierarchical path within a category tree.
 * @param {Object} categoriesTree - The root object containing the categories array.
 * @returns {Object} An object where each key is a category ID and the value is an array representing the path of IDs from the root to that category.
 */
const buildCategoryLookup = (categoriesTree) => {
    const categoryLookup = {}

    const traverseCategories = (categories, path = []) => {
        for (const category of categories) {
            const newPath = [...path, category.id] // Store only the category ID
            categoryLookup[category.id] = newPath

            if (category?.categories) {
                traverseCategories(category.categories, newPath)
            }
        }
    }

    if (categoriesTree) {
        traverseCategories(categoriesTree.categories)
    }
    return categoryLookup
}
